import React, { useState, useEffect, useRef } from 'react'
import { Box, Typography, IconButton, Slider, Stack, Fade } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'

const AudioPlayer = ({ src }) => {
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.7)
  const [muted, setMuted] = useState(false)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const [equalizer, setEqualizer] = useState([0.3, 0.5, 0.7, 0.4, 0.6])
  const [isInitialized, setIsInitialized] = useState(false)
  const [buffered, setBuffered] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  const audioRef = useRef(null)
  const animationRef = useRef(null)
  const sliderRef = useRef(null)
  const currentTimeRef = useRef(0)
  const lastUpdateTimeRef = useRef(0)
  const wasPlayingRef = useRef(false)
  const volumeChangeTimeoutRef = useRef(null)

  const safePlay = async () => {
    const audio = audioRef.current
    if (!audio || !isInitialized) return false

    try {
      await audio.play()
      return true
    } catch (error) {
      console.error('Error playing audio:', error)
      return false
    }
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.volume = volume
    audio.muted = muted
    setIsLoading(true)

    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
      setIsInitialized(true)
      setIsLoading(false)
    }

    const handleLoadedData = () => {
      setIsLoading(false)
    }

    const handleWaiting = () => {
      setIsLoading(true)
    }

    const handleCanPlay = () => {
      setIsLoading(false)
    }

    const handleEnded = () => {
      setPlaying(false)
      setCurrentTime(0)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }

    const handleError = e => {
      console.error('Error loading audio:', e)
      setIsInitialized(false)
      setIsLoading(false)
      setPlaying(false)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }

    const handleProgress = () => {
      if (!audio || audio.buffered.length === 0) return

      let currentBuffered = 0

      for (let i = 0; i < audio.buffered.length; i++) {
        const start = audio.buffered.start(i)
        const end = audio.buffered.end(i)

        if (start <= audio.currentTime && audio.currentTime <= end) {
          currentBuffered = end
          break
        }

        if (audio.currentTime < start) {
          currentBuffered = 0
          break
        }
      }

      setBuffered(currentBuffered)
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('loadeddata', handleLoadedData)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('canplay', handleCanPlay)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)
    audio.addEventListener('progress', handleProgress)
    audio.addEventListener('timeupdate', handleProgress)

    if (src) {
      audio.load()
    }

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('loadeddata', handleLoadedData)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('canplay', handleCanPlay)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('progress', handleProgress)
      audio.removeEventListener('timeupdate', handleProgress)

      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }

      if (volumeChangeTimeoutRef.current) {
        clearTimeout(volumeChangeTimeoutRef.current)
        volumeChangeTimeoutRef.current = null
      }
    }
  }, [src])

  useEffect(() => {
    setCurrentTime(0)
    setPlaying(false)
    setIsInitialized(false)
    setBuffered(0)
    setIsLoading(true)

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
  }, [src])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.volume = volume
    audio.muted = muted
  }, [volume, muted])

  useEffect(() => {
    if (playing) {
      const interval = setInterval(() => {
        setEqualizer(prev => prev.map(() => 0.2 + Math.random() * 0.8))
      }, 200)
      return () => clearInterval(interval)
    }
  }, [playing])

  const togglePlay = async () => {
    const audio = audioRef.current
    if (!audio || !isInitialized) return

    if (playing) {
      audio.pause()
      setPlaying(false)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    } else {
      const success = await safePlay()
      setPlaying(success)

      if (success && !animationRef.current) {
        lastUpdateTimeRef.current = performance.now()
        animationRef.current = requestAnimationFrame(updateProgress)
      }
    }
  }

  const updateProgress = () => {
    const audio = audioRef.current
    if (!audio) return

    currentTimeRef.current = audio.currentTime

    const now = performance.now()
    if (now - lastUpdateTimeRef.current > 50) {
      setCurrentTime(audio.currentTime)
      lastUpdateTimeRef.current = now
    }

    if (sliderRef.current) {
      const sliderValue = audio.duration > 0 ? (audio.currentTime / audio.duration) * 100 : 0

      const thumbElement = sliderRef.current.querySelector('.MuiSlider-thumb')
      const trackElement = sliderRef.current.querySelector('.MuiSlider-track')

      if (thumbElement && trackElement) {
        const thumbPosition = `${sliderValue}%`
        thumbElement.style.left = thumbPosition
        trackElement.style.width = thumbPosition
      }
    }

    if (audio.paused || audio.ended) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
      return
    }

    animationRef.current = requestAnimationFrame(updateProgress)
  }

  const handleSeek = async (_, newValue) => {
    const audio = audioRef.current
    if (!audio || !isInitialized) return

    const wasPlaying = !audio.paused

    audio.currentTime = newValue
    setCurrentTime(newValue)

    if (wasPlaying || !playing) {
      const success = await safePlay()
      setPlaying(success)

      if (success && !animationRef.current) {
        lastUpdateTimeRef.current = performance.now()
        animationRef.current = requestAnimationFrame(updateProgress)
      }
    }
  }

  const handleVolumeChange = (_, newValue) => {
    const audio = audioRef.current
    if (!audio) return

    const volumeValue = newValue / 100

    wasPlayingRef.current = playing

    setVolume(volumeValue)
    audio.volume = volumeValue

    if (volumeValue > 0 && muted) {
      setMuted(false)
      audio.muted = false
    }

    if (volumeChangeTimeoutRef.current) {
      clearTimeout(volumeChangeTimeoutRef.current)
    }

    volumeChangeTimeoutRef.current = setTimeout(() => {
      if (wasPlayingRef.current && audio.paused) {
        safePlay().then(success => {
          if (!success) setPlaying(false)
        })
      }
      volumeChangeTimeoutRef.current = null
    }, 200)
  }

  const toggleMute = async () => {
    const audio = audioRef.current
    if (!audio) return

    wasPlayingRef.current = playing

    const newMuted = !muted
    audio.muted = newMuted
    setMuted(newMuted)

    if (wasPlayingRef.current && audio.paused) {
      const success = await safePlay()
      if (!success) setPlaying(false)
    }
  }

  const formatTime = time => {
    if (isNaN(time)) return '00:00'

    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <Box
      sx={{
        p: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderRadius: '16px',
        overflow: 'visible',
        position: 'relative',
        border: '1px solid rgba(0,0,0,0.05)'
      }}
    >
      <audio ref={audioRef} src={src} preload='metadata' />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Fade in={playing}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                height: 20,
                width: 30,
                position: 'relative',
                zIndex: 1000,
                transform: 'translateZ(0)'
              }}
            >
              {equalizer.map((value, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 4,
                    mx: 0.5,
                    height: `${value * 100}%`,
                    backgroundColor: 'primary.main',
                    transition: 'height 0.2s ease'
                  }}
                />
              ))}
            </Box>
          </Fade>

          <Box
            sx={{
              position: 'relative',
              zIndex: 200
            }}
            onMouseEnter={() => setShowVolumeSlider(true)}
            onMouseLeave={() => setShowVolumeSlider(false)}
          >
            <IconButton size='small' onClick={toggleMute} color='primary'>
              {muted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>

            <Fade in={showVolumeSlider}>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  p: 1,
                  width: 36,
                  height: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  backgroundColor: 'white',
                  borderRadius: '8px'
                }}
              >
                <Slider
                  orientation='vertical'
                  value={volume * 100}
                  onChange={handleVolumeChange}
                  aria-label='Volume'
                  min={0}
                  max={100}
                  sx={{ height: 80 }}
                />
              </Box>
            </Fade>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, position: 'relative', zIndex: 1 }}>
        <IconButton
          onClick={togglePlay}
          sx={{
            width: 60,
            height: 60,
            backgroundColor: isLoading ? 'rgba(25, 118, 210, 0.6)' : 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            transition: 'all 0.3s ease'
          }}
          disabled={!isInitialized || isLoading}
        >
          {playing ? <PauseIcon fontSize='large' /> : <PlayArrowIcon fontSize='large' />}
        </IconButton>
      </Box>

      <Box sx={{ mb: 1, position: 'relative', zIndex: 1 }} ref={sliderRef}>
        <Slider
          value={currentTime || 0}
          max={duration > 0 ? duration : 100}
          onChange={handleSeek}
          disabled={!isInitialized || isLoading}
          aria-label='Audio progress'
          sx={{
            color: 'primary.main',
            height: 4,
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
              transition: 'transform 0.05s cubic-bezier(.47,1.64,.41,.8)',
              willChange: 'transform',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0px 0px 0px 8px rgba(25, 118, 210, 0.16)'
              },
              '&.Mui-active': {
                width: 16,
                height: 16
              }
            },
            '& .MuiSlider-rail': {
              opacity: 0.28
            },
            '& .MuiSlider-track': {
              transition: 'none',
              willChange: 'width'
            },
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden'
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant='caption' color='text.secondary'>
          {formatTime(currentTime)}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {formatTime(duration)}
        </Typography>
      </Box>
    </Box>
  )
}

export default AudioPlayer
